import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
// import DynamicTable from "../../../../components/Dynamic-table";
import DynamicTable from "../../../../components/lab-dynamic-table";
import {
  getDefaultRecordDeleteTooltip,
  getDefaultRecordEditTooltip,
  isDefaultLabRecord,
} from "../../utils/defaultRecordActions";

import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";
// Enum for measuring types
export enum MEASURING_TYPES {
  Mass = "Mass",
  Liquid = "Liquid",
  Gas = "Gas",
  Count = "Count",
}

const TestUnits: React.FC<any> = () => {
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState<any>("");
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [dosageData, setDosageData] = useState<any>();
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    unitName: "",
    measuringType: "",
    abbreviation: "",
    isSmallestUnit: false,
  });

  const navigate = useNavigate();
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/testUnits/getAllByPagination`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
            search: searchInput,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDosageData(response.data?.data);
      setTotal(response.data?.totalCount);
    } catch (error:any) {
      console.error("Error fetching data:", error);
      toast.error(error?.response.data.message || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };


  // Handle excel file upload
  const handleExcelFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!["xls", "xlsx"].includes(fileExtension || "")) {
      toast.error("Please upload a valid Excel file (.xls or .xlsx)");
      e.target.value = ""; // reset input if invalid
      return;
    }

    const data = new FormData();
    data.append("file", file);

    try {
      setUploading(true); // ✅ start loader

      const res = await axios.post(
        `${process.env.REACT_APP_LAB_URL}/import-data/fileUpload`,
        data,
        {
          params: {entity: "units"},
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      console.log("✅ excel file upload", res);
      toast.success(res.data?.message || "File uploaded successfully");
      fetchData(); // Refresh data after successful upload

      e.target.value = ""; // reset input
    } catch (error: any) {
      console.error("❌ Error uploading excel file", error);
      toast.info(
        error.response?.data?.message ||
          error.message ||
          "Failed to upload file"
      );
      e.target.value = "";
    } finally {
      setUploading(false); // ✅ stop loader
    }
  };


  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (id: any, rowData: any) => {
    setEditData(id);
    setFormData({
      unitName: rowData?.unitName,
      measuringType: rowData?.measuringType,
      abbreviation: rowData?.abbreviation,
      isSmallestUnit: rowData?.isSmallestUnit,
    });
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, debouncedSearch]);

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authorization token is missing");
        return;
      }

      const response = await axios.delete(
        `${process.env.REACT_APP_LAB_URL}/testUnits/deleteById/${selectedItemId}`,

        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchData();
      toast.success(response.data.message);
      setFormData({
        unitName: "",
        measuringType: "",
        abbreviation: "",
        isSmallestUnit: false,
      });
      setEditData(null);
    } catch (error: any) {
      toast.error(error.message);
    }
    setIsDeleteModalOpen(false);
  };

  const handleSubmit = async () => {
    if (editData) {
      try {
        const response = await axios.patch(
          `${process.env.REACT_APP_LAB_URL}/testUnits/updateById/${editData}`,
          {
            unitName: formData.unitName,
            measuringType: formData.measuringType,
            abbreviation: formData.abbreviation,
            isSmallestUnit: formData.isSmallestUnit,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          setFormData({
            unitName: "",
            measuringType: "",
            abbreviation: "",
            isSmallestUnit: false,
          });
          fetchData();
          setEditData(null);
        }
      } catch (error: any) {
        toast.error(error.message || "Something went wrong");
      }
    } else {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_LAB_URL}/testUnits/addUnit`,
          {
            unitName: formData.unitName,
            measuringType: formData.measuringType,
            abbreviation: formData.abbreviation,
            isSmallestUnit: formData.isSmallestUnit,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          setFormData({
            unitName: "",
            measuringType: "",
            abbreviation: "",
            isSmallestUnit: false,
          });
          fetchData();
          setEditData(null);
        }
      } catch (error: any) {
        toast.error(error.message || "Something went wrong");
      }
    }
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteClick = (id: any) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };
  const handleChangeDropdown = (e: SelectChangeEvent<string>) => {
    setFormData((prev) => ({
      ...prev,
      measuringType: e.target.value,
    }));
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
          }}
        >
          Test Units
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Search"
            placeholder="Search Unit Name..."
            variant="outlined"
            value={searchInput}
            // className="search-field"
            className="custom-input"
            onChange={handleSearchInputChange}
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={1}>
        {/* Form Section */}
        <Grid item xs={12} sm={4} md={4}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 3,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {editData ? "Update Test Unit" : "Add New Test Unit"}
            </Typography>

            <TextField
              size="small"
              label="Unit Name"
              name="unitName"
              // className="search-field"
              className="custom-input"
              value={formData.unitName}
              fullWidth
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <Select
              size="small"
              name="measuringType"
              value={formData.measuringType}
              onChange={handleChangeDropdown}
              fullWidth
              displayEmpty
              sx={{
                mb: 2,
              }}
              // className="search-field"
              className="custom-input"
            >
              <MenuItem value="">
                <Typography color="#686464ff">Measuring Type</Typography>
              </MenuItem>
              {Object.values(MEASURING_TYPES).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>

            <TextField
              size="small"
              label="Abbreviation"
              // className="search-field"
              className="custom-input"
              name="abbreviation"
              value={formData.abbreviation}
              fullWidth
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isSmallestUnit}
                  onChange={handleChange}
                  name="isSmallestUnit"
                  // className="search-field"
                  className="custom-input"
                />
              }
              label="Smallest Unit"
            />

            <Button
              variant="contained"
              color="primary"
              sx={{ marginTop: 1 }}
              fullWidth
              onClick={handleSubmit}
              disabled={!formData.unitName || !formData.abbreviation}
            >
              {editData ? "Update" : "Save"}
            </Button>

            <div className="w-full mt-4 flex justify-between items-center gap-2">
              <div>
                <input
                  type="file"
                  accept=".xls,.xlsx"
                  id="excel-upload"
                  onChange={handleExcelFileUpload}
                  style={{ display: "none" }} // hide the default file input
                />
                <label htmlFor="excel-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    disabled={uploading} // ✅ disable while uploading
                    startIcon={
                      uploading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <img
                          src="/excel-logo.png"
                          alt="Excel File"
                          style={{ width: 24, height: 24 }}
                        />
                      )
                    }
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px",
                      padding: "8px 16px",
                    }}
                  >
                    {uploading
                      ? "Uploading..."
                      : "Import Excel File (.xls / .xlsx)"}
                  </Button>
                </label>
              </div>
              <Tooltip arrow placement="top" title="Download Excel Template">
                <IconButton
                  sx={{
                    // backgroundColor: "blue",
                    color: "white",
                    "&:hover": {
                      // backgroundColor: "darkgreen",
                    },
                  }}
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = "/Test Unit Template.xlsx";
                    link.download = "Test Unit Template.xlsx";
                    link.click();
                  }}
                >
                  {/* <DescriptionIcon /> */}
                  <img
                    src="/export-excel.png" // ✅ directly from public folder
                    alt="Excel File"
                    style={{ width: 30, height: 30 }}
                  />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
        </Grid>

        {/* Table Section */}
        <Grid item xs={12} sm={8} md={8}>
          <DynamicTable
            headers={[
              "SN",
              "Unit Name",
              "Measuring Type",
              "Abbreviation",
              "Smallest Unit",
              "Actions",
            ]}
            data={dosageData?.map((item: any, index: any) => [
              page * rowsPerPage + index + 1,
              item?.unitName || "",
              item?.measuringType || "",
              item?.abbreviation || "",
              item?.isSmallestUnit ? "Yes" : "No",
            ])}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRows={total}
            actionButtons={[
              {
                icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
                onClick: (rowIndex: any) =>
                  handleEditClick(
                    dosageData[rowIndex]?._id,
                    dosageData[rowIndex]
                  ),
                disabled: (rowIndex) => isDefaultLabRecord(dosageData[rowIndex]),
                tooltip: (rowIndex) =>
                  getDefaultRecordEditTooltip(dosageData[rowIndex]),
              },
              {
                icon: (
                  <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) =>
                  handleDeleteClick(dosageData[rowIndex]?._id),
                disabled: (rowIndex) => isDefaultLabRecord(dosageData[rowIndex]),
                tooltip: (rowIndex) =>
                  getDefaultRecordDeleteTooltip(dosageData[rowIndex]),
              },
            ]}
            height={"60vh"}
          />
          {dosageData?.length === 0 && (
            <Typography
              sx={{
                color: "gray",
                mt: 2,
                textAlign: "center",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              No data found
            </Typography>
          )}
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default TestUnits;
